/**
 * LevelSelector - Main entry point for Aura City of Lights.
 * Levels are gated by Learning path completion (Level 1 always open; 2 = basic complete; 3 = intermediate complete).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { LockClosedIcon, CheckCircleIcon, AcademicCapIcon } from '@heroicons/react/24/outline';

const LevelSelector = ({ user }) => {
  const navigate = useNavigate();
  const [unlocks, setUnlocks] = useState(null);
  const [learning, setLearning] = useState({ completed_paths: [], completed_count: 0, total_paths: 3 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUnlocks();
  }, []);

  const loadUnlocks = async () => {
    try {
      const response = await axios.get('/api/game/city-of-signals/unlocks');
      setUnlocks(response.data.unlocks || {});
      setLearning(response.data.learning || { completed_paths: [], completed_count: 0, total_paths: 3 });
    } catch (err) {
      console.error('Error loading unlocks:', err);
      setUnlocks({});
    } finally {
      setLoading(false);
    }
  };

  const levels = [
    {
      id: 'level_1',
      name: 'Level 1 – Reading the Room',
      description: 'Learn to read signals and listen to different perspectives.',
      unlockBlurb: 'Available to everyone. Start here.',
      districts: ['Income', 'Stability', 'Regulation'],
      turns: 3,
      unlockKey: 'level_1',
      reasonKey: 'level_1_reason'
    },
    {
      id: 'level_2',
      name: 'Level 2 – Managing Tradeoffs',
      description: 'Navigate complex decisions with multiple stakeholders.',
      unlockBlurb: 'Unlocks after completing Basic Financial Literacy in Learning.',
      districts: 'All Districts',
      turns: 5,
      unlockKey: 'level_2',
      reasonKey: 'level_2_reason'
    },
    {
      id: 'level_3',
      name: 'Level 3 – System Mastery',
      description: 'Master the art of signal reading with hidden information.',
      unlockBlurb: 'Unlocks after completing Intermediate Investing in Learning.',
      districts: 'All Districts',
      turns: 5,
      unlockKey: 'level_3',
      reasonKey: 'level_3_reason'
    }
  ].map((level) => ({
    ...level,
    unlocked: unlocks ? unlocks[level.unlockKey] === true : level.id === 'level_1',
    unlockReason: unlocks ? unlocks[level.reasonKey] : null
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone text-primary p-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 pb-8 border-b border-hairline">
          <h1 className="font-display text-h1 text-primary mb-4">Aura City of Lights</h1>
          <p className="text-body text-secondary mb-4 max-w-2xl mx-auto">
            A character-driven strategy game about reading signals and making decisions. Progress in
            Learning unlocks more levels.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-hairline bg-bone-raised text-label text-secondary">
            <AcademicCapIcon className="w-5 h-5 text-pine" />
            <span>
              Your learning progress:{' '}
              <span className="figure text-primary">{learning.completed_count}</span> of{' '}
              <span className="figure text-primary">{learning.total_paths}</span> paths complete
            </span>
            <Link to="/learning" className="text-pine hover:underline ml-1">
              Go to Learning
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {levels.map((level, index) => (
            <motion.div
              key={level.id}
              className={`rounded-lg p-6 border ${
                level.unlocked
                  ? 'bg-bone-raised border-hairline cursor-pointer hover:border-pine'
                  : 'bg-bone border-hairline opacity-60 cursor-not-allowed'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => {
                if (level.unlocked) {
                  navigate(`/game/city-of-signals/${level.id}`);
                }
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-h3 text-primary">{level.name}</h2>
                {level.unlocked ? (
                  <CheckCircleIcon className="w-6 h-6 text-pine" />
                ) : (
                  <LockClosedIcon className="w-6 h-6 text-ink-muted" />
                )}
              </div>
              <p className="text-body text-secondary mb-2">{level.description}</p>
              <p className="text-label text-pine mb-4">{level.unlockBlurb}</p>
              <div className="text-label text-secondary space-y-1">
                <div>
                  Districts:{' '}
                  {Array.isArray(level.districts) ? level.districts.join(', ') : level.districts}
                </div>
                <div>
                  Turns: <span className="figure text-primary">{level.turns}</span>
                </div>
              </div>
              {!level.unlocked && level.unlockReason && (
                <p className="mt-3 text-label text-secondary border border-hairline rounded-md p-2 bg-pine-tint">
                  {level.unlockReason}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LevelSelector;
